import type { ScenarioPersonaDto } from '@/api-gen/api.schemas';
import { PersonaUsage } from '@/api-gen/api.schemas';

export type AssistantTurnInterruptionMetrics = {
  heardFraction?: number | null;
  heardDurationMs?: number | null;
  totalDurationMs?: number | null;
};

const getFiniteNumber = (value: number | null | undefined): number | null =>
  typeof value === 'number' && Number.isFinite(value) ? value : null;

const getHeardFraction = (metrics: AssistantTurnInterruptionMetrics): number | null => {
  const heardFraction = getFiniteNumber(metrics.heardFraction);
  if (heardFraction !== null) {
    return Math.min(1, Math.max(0, heardFraction));
  }

  const heardDurationMs = getFiniteNumber(metrics.heardDurationMs);
  const totalDurationMs = getFiniteNumber(metrics.totalDurationMs);
  if (heardDurationMs === null || totalDurationMs === null || totalDurationMs <= 0) {
    return null;
  }

  return Math.min(1, Math.max(0, heardDurationMs / totalDurationMs));
};

export const getBestEffortHeardText = (
  text: string | null | undefined,
  metrics: AssistantTurnInterruptionMetrics
): string => {
  const fullText = text?.trim() ?? '';
  const heardFraction = getHeardFraction(metrics);
  if (!fullText || heardFraction === null || heardFraction <= 0) {
    return '';
  }

  if (heardFraction >= 0.995) {
    return fullText;
  }

  const targetLength = Math.floor(fullText.length * heardFraction);
  if (targetLength <= 0) {
    return '';
  }

  const roughText = fullText.slice(0, targetLength);
  const lastWhitespace = Math.max(roughText.lastIndexOf(' '), roughText.lastIndexOf('\n'), roughText.lastIndexOf('\t'));
  const minBoundary = Math.floor(targetLength * 0.4);
  const heardText = lastWhitespace >= minBoundary ? roughText.slice(0, lastWhitespace) : roughText;

  return heardText.trim();
};

/**
 * Heard text for an interrupted assistant turn. Breathing isn't heard word by word, so an interrupted
 * breathing turn keeps its whole line instead of a fragment such as "(breath". Otherwise the text is cut
 * at the heard fraction, or null when the fraction is unknown.
 */
export const getInterruptedTurnHeardText = (
  turn: { finalText?: string | null; persona?: Pick<ScenarioPersonaDto, 'personaUsages'> | null },
  metrics: AssistantTurnInterruptionMetrics
): string | null => {
  if (turn.persona?.personaUsages?.includes(PersonaUsage.AgonalBreathing)) {
    return turn.finalText?.trim() ?? '';
  }

  return metrics.heardFraction === null ? null : getBestEffortHeardText(turn.finalText, metrics);
};
